import { type ApiErrorResult, type ApiResult } from '@scoring/api';
import { QueryClient, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { logApiError } from './errorInterceptor';

// A generic Query API hook using React Query
// transform functions are used to process the fetched data
export interface UseApiQueryParams<TResponse, TData> {
    queryKey: QueryKey;
    queryFn: (queryKey?: QueryKey) => Promise<ApiResult<TResponse>>;
    transform?: (response: TResponse, queryClient: QueryClient) => TData;
    queryOptions?: QueryOptions;
}

export interface QueryOptions {
    enabled?: boolean;
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number;
    staleTime?: number;
    retry?: boolean | number;
}

export const useApiQuery = <TResponse, TData = TResponse>(params: UseApiQueryParams<TResponse, TData>) => {
    const {
        queryKey,
        queryFn,
        transform = (data: TResponse) => data as unknown as TData,
        queryOptions = {
            enabled: true,
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchInterval: 1 * 60 * 1000, // 1 minute
            staleTime: undefined,
        },
    } = params;
    const normalizedQueryKey = useMemo(() => (Array.isArray(queryKey) ? queryKey : [queryKey]), [queryKey]);

    const queryClient = useQueryClient();

    function _sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const query = useQuery<TData, ApiErrorResult>({
        queryKey: normalizedQueryKey,
        queryFn: async () => {
            const result = await queryFn();
            // await _sleep(3000);
            return transform(result.data, queryClient);
        },
        placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
        enabled: queryOptions.enabled,
        refetchOnMount: queryOptions.refetchOnMount,
        refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
        refetchInterval: queryOptions.refetchInterval,
        staleTime: queryOptions.staleTime,
        retry: queryOptions.retry,
    });

    // Track the last error we logged to avoid duplicate logging
    const lastLoggedErrorRef = useRef<ApiErrorResult | null>(null);

    // Log error if it's changed
    useEffect(() => {
        if (query.error && query.error !== lastLoggedErrorRef.current) {
            lastLoggedErrorRef.current = query.error;
            logApiError(query.error, 'query', { queryKey: normalizedQueryKey });
        }
    }, [query.error, normalizedQueryKey]);

    return { ...query };
};
